import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router';
import { useAppDispatch, useAppSelector } from '../../store';
import { SectionHeader } from '../common/SectionHeader';
import { getWorkplaceTrainings, selectWorkplaceTrainings } from '../../store/entities/workplaceTrainings';

import { closestCenter, DndContext, DragEndEvent, KeyboardSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { arrayMove, SortableContext, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { TableRow } from '@mui/material';
import { QcsPaper } from '../common/basic/QcsPaper';
import { AddTrainingModal } from './AddTrainingModal';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import { QcsLoadingButton } from '../common/basic/QcsLoadingButton';
import styled, { CSSObject } from '@emotion/styled';
import { QcsTableContainer } from '../common/basic/QcsTableContainer';
import { QcsTableHead } from '../common/basic/QcsTableHead';
import { QcsTableRow } from '../common/basic/QcsTableRow';
import { QcsTableCell } from '../common/basic/QcsTableCell';
import { QcsTableBody } from '../common/basic/QcsTableBody';
import { CellDate } from '../common/grid/CellDate';
import { getLangNameObj } from '../../utils/format';
import { hasRole } from '../../utils/roles';
import { selectIdentity } from '../../store/entities/identity';
import { getWorkplaceList, workplaceListActions } from '../../store/entities/workplaceList';
import axios from 'axios';
import { workplaceService } from '@api/services/services';
import { TrainingDto } from '@api/models/training/TrainingDto';
import { ShowError } from '../ShowError';
import { ShowQcsTableContent } from '../ShowQcsTableContent';
import { resources } from '@api/resources';
import { UserRole } from '@api/models/enums/UserRole';

const SaveButtonWrapper = styled.div(
  ({ theme }): CSSObject => ({
    display: 'flex',
    justifyContent: 'end',
    marginTop: '1rem',
    gap: '1rem',
    flexDirection: 'column',
    [theme.breakpoints.up('sm')]: {
      flexDirection: 'row',
    },
  })
);

interface SortableRowProps {
  id: string;
  children: React.ReactNode;
  onClick: () => void;
}

const SortableRow: FC<SortableRowProps> = ({ id, children, onClick }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    userSelect: 'none' as const,
    backgroundColor: isDragging ? '#f0f0f0' : undefined,
  };

  return (
    <TableRow ref={setNodeRef} style={style} onClick={onClick} {...attributes} {...listeners}>
      {children}
    </TableRow>
  );
};

export const WorkplaceTraining: FC = () => {
  const { t, i18n } = useTranslation();
  const { workplaceId } = useParams();
  const identity = useAppSelector(selectIdentity);
  const workplaceTrainings = useAppSelector(selectWorkplaceTrainings);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [adding, setAdding] = useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const loadTrainings = useCallback(() => {
    dispatch(getWorkplaceTrainings(workplaceId!));
  }, [dispatch, workplaceId]);

  const updateWorkplaces = useCallback(() => {
    dispatch(workplaceListActions.reload(true));
    const cancelToken = axios.CancelToken.source().token;
    dispatch(getWorkplaceList(cancelToken));
  }, [dispatch]);

  useEffect(() => {
    loadTrainings();
  }, [loadTrainings]);

  const [items, setItems] = useState<TrainingDto[]>([]);
  useEffect(() => {
    setItems(workplaceTrainings);
  }, [workplaceTrainings]);

  const handleSaveOrder = async () => {
    await workplaceService.changeTrainingOrder(workplaceId!, { entries: items.map((x) => x.id!) });
    loadTrainings();
  };

  const handleRemove = (trainingId: string) => async (event: any) => {
    event.stopPropagation();
    await workplaceService.removeTrainingFromWorkplace(workplaceId!, trainingId);
    loadTrainings();
    updateWorkplaces();
  };

  const handleRowClick = (trainingId: string) => () => {
    if (hasRole(identity.roles, [UserRole.AdminQcs, UserRole.ManagerOsah])) {
      navigate(`/training/${trainingId}`, {
        state: { back: `/workplace/${workplaceId}` },
      });
    }
  };

  const handleAddClick = () => {
    setAdding(true);
  };

  const handleCloseAdding = () => {
    setAdding(false);
  };

  const onDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (!over || active.id === over.id) {
      return;
    }

    const oldIndex = items.findIndex((item) => item.id === active.id);
    const newIndex = items.findIndex((item) => item.id === over.id);

    const result = arrayMove(items, oldIndex, newIndex);
    setItems(result);
  };

  const workplaceTrainingIds = useMemo(() => workplaceTrainings.map((x) => x.id!), [workplaceTrainings]);

  return (
    <>
      <AddTrainingModal
        open={adding}
        onClose={handleCloseAdding}
        reloadTrainings={() => {
          updateWorkplaces();
          loadTrainings();
        }}
        excludeIds={workplaceTrainingIds}
      />

      <SectionHeader title="workplaceTraining.title" addText="workplaceTraining.add" handleAddClick={handleAddClick} />

      <QcsPaper>
        <QcsTableContainer sx={{ overflow: 'unset' }}>
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
            <ShowQcsTableContent resource={resources.getTrainingsByWorkplace} data={workplaceTrainings}>
              <QcsTableHead>
                <QcsTableRow>
                  <QcsTableCell>{t('workplaceTraining.name')}</QcsTableCell>
                  <QcsTableCell>{t('workplaceTraining.languageVersions')}</QcsTableCell>
                  <QcsTableCell hideOnMobile>{t('workplaceTraining.latestValidFrom')}</QcsTableCell>
                  <QcsTableCell hideOnMobile>{t('workplaceTraining.latestValidTo')}</QcsTableCell>
                  <QcsTableCell>{t('workplaceTraining.remove')}</QcsTableCell>
                </QcsTableRow>
              </QcsTableHead>
              <SortableContext items={items.map((item) => item.id!)} strategy={verticalListSortingStrategy}>
                <QcsTableBody>
                  {items.map((item) => (
                    <SortableRow key={item.id} id={item.id!} onClick={handleRowClick(item.id!)}>
                      <QcsTableCell sx={{ width: '20rem' }}>{getLangNameObj(i18n, item)}</QcsTableCell>
                      <QcsTableCell>{item.trainingLanguages?.join(', ')}</QcsTableCell>
                      <CellDate value={item.latestValidFrom} hideOnMobile />
                      <CellDate value={item.latestValidTo} hideOnMobile />

                      <QcsTableCell sx={{ width: '2rem' }}>
                        <QcsLoadingButton onClick={handleRemove(item.id!)} resourceLoading={resources.removeTrainingFromWorkplace}>
                          <DeleteForeverIcon color="error" />
                        </QcsLoadingButton>
                      </QcsTableCell>
                    </SortableRow>
                  ))}
                </QcsTableBody>
              </SortableContext>
            </ShowQcsTableContent>
          </DndContext>
        </QcsTableContainer>
      </QcsPaper>

      <SaveButtonWrapper>
        <ShowError resource={resources.changeTrainingOrder} />
        <QcsLoadingButton variant="contained" onClick={handleSaveOrder} resourceLoading={resources.changeTrainingOrder}>
          {t('workplaceTraining.saveOrder')}
        </QcsLoadingButton>
      </SaveButtonWrapper>
    </>
  );
};
